import React from 'react';
import PropTypes from 'prop-types';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import BeachAccessIcon from '@material-ui/icons/BeachAccess';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import SchoolIcon from '@material-ui/icons/School';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import Avatar from 'Components/ComponentLibrary/Atoms/Avatar';
import Accordion from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Accordion';
import AccordionDetails from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/AccordionDetails';
import AccordionSummary from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/AccordionSummary';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import MetaControl from 'Components/UI/Meta/MetaControl';
import metaOverrideSet from './metaOverrideSet';

const styles = theme => {
  const leftGutterWidth = `${theme.spacing(6)}px`;

  return {
    accordion: {
      borderRadius: `${theme.radius.md}px !important`,
      boxShadow: 'none',
      '&::before': {
        display: 'none',
      },
      '& .MuiAccordionDetails-root': {
        padding: `0 ${theme.spacing(1)}px ${theme.spacing(2)}px`,
      },
      '& .MuiAccordionSummary-root': {
        padding: `0 ${theme.spacing(1)}px`,
      },
      '& .MuiAccordionSummary-content': {
        minWidth: 0,
      },
    },
    accordionWrapper: {
      flexGrow: '1',
      minWidth: 0,
    },
    avatar: {
      backgroundColor: 'transparent',
      color: theme.palette.facet.mediumGray2,
      width: leftGutterWidth,
    },
    title: {
      marginTop: theme.spacing(1),
    },
    inline: {
      display: 'flex',
      gap: `${theme.spacing(1)}px`,
    },
    wrapper: {
      alignItems: 'center',
      display: 'flex',
      justifyItems: 'center',
      position: 'relative',
      width: '100%',
    },
    detailValuesContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: `${theme.spacing(2)}px`,
      marginLeft: leftGutterWidth,
    },
    summaryContainer: {
      display: 'flex',
      width: '100%',
    },
    summaryAvatarItem: {
      display: 'flex',
      justifyItems: 'center',
    },
    summaryContentItem: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: '1',
      gap: `${theme.spacing(1)}px`,
      minWidth: 0,
    },
    summaryValuesContainer: {
      display: 'flex',
      gap: `${theme.spacing(2)}px`,
      flexWrap: 'wrap',
    },
  };
};

export const getOutcomeSummaryDetailIcon = resource => {
  switch (resource.attr('outcomeType')) {
    case 'education':
      return SchoolIcon;
    case 'emergencyFund':
      return BeachAccessIcon;
    case 'generalSavings':
      return AttachMoneyIcon;
    case 'payOffDebt': {
      if (/vehicle/i.test(resource.attr('title'))) {
        return DirectionsCarIcon;
      }
      if (/student/i.test(resource.attr('title'))) {
        return AccountBalanceIcon;
      }
      return CreditCardIcon;
    }
    case 'retirement':
      return TrendingUpIcon;
    default:
      return () => null;
  }
};

const OutcomeCard = ({ classes, outcomeSummaryDetailResource }) => {
  const Icon = getOutcomeSummaryDetailIcon(outcomeSummaryDetailResource);

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.accordionWrapper}>
        <Accordion className={classes.accordion}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
            <Box className={classes.summaryContainer}>
              <Box className={classes.summaryAvatarItem}>
                <Avatar avatar={<Icon />} classes={{ root: classes.avatar }} />
              </Box>
              <Box className={classes.summaryContentItem}>
                <Box className={classes.title}>
                  <MetaControl
                    attrName="title"
                    resource={outcomeSummaryDetailResource}
                    metaOverrideSet={metaOverrideSet}
                  />
                </Box>
                <Box className={classes.summaryValuesContainer}>
                  {outcomeSummaryDetailResource
                    .attr('summaryValues')
                    .resources()
                    .list()
                    .map(resource => (
                      <Box key={resource.id()}>
                        <MetaControl
                          attrName="value"
                          resource={resource}
                          metaOverrideSet={metaOverrideSet}
                        />
                      </Box>
                    ))}
                </Box>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box className={classes.detailValuesContainer}>
              {outcomeSummaryDetailResource
                .attr('detailValues')
                .resources()
                .list()
                .map(resource => (
                  <Box key={resource.id()}>
                    <MetaControl
                      attrName="value"
                      resource={resource}
                      metaOverrideSet={metaOverrideSet}
                    />
                  </Box>
                ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

OutcomeCard.defaultProps = {};

OutcomeCard.propTypes = {
  classes: PropTypes.object.isRequired,
  outcomeSummaryDetailResource: PropTypes.object.isRequired,
};

export default withStyles(styles)(OutcomeCard);
